
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Message, ChatHistory } from "@/types/posit";
import { MessageCircle, ArrowRight } from "lucide-react";
import ChatMessage from "@/components/posit/ChatMessage";
import { jsonToFileAttachment } from "@/utils/typeConversions";
import { Json } from "@/integrations/supabase/types";

const ChatDetails = () => {
  const { chatId } = useParams();
  const { user } = useAuth();

  const { data: chatData, isLoading: isLoadingChat } = useQuery({
    queryKey: ["chat-details", chatId],
    queryFn: async () => {
      if (!chatId || !user?.id) return null;

      const { data: chatHistory, error: chatError } = await supabase
        .from("chat_history")
        .select(`
          id,
          user_id,
          created_at,
          last_message_at,
          summary,
          posits (
            id,
            title,
            user_id
          )
        `)
        .eq("id", chatId)
        .maybeSingle();

      if (chatError) throw chatError;
      if (!chatHistory) return null;

      // Enforce access: only the chatter (chat_history.user_id) or the posit owner (posits.user_id)
      const isChatter = chatHistory.user_id === user.id;
      const isOwner = (chatHistory as any)?.posits?.user_id === user.id;
      if (!isChatter && !isOwner) {
        return null;
      }
      
      // If there's no summary, generate one
      if (!chatHistory.summary) {
        const { data: messages } = await supabase
          .from("chat_messages")
          .select("content, role")
          .eq("posit_id", chatHistory.posits.id)
          .eq("source", "posit_query")
          .gte("created_at", chatHistory.created_at)
          .lte("created_at", chatHistory.last_message_at)
          .order("created_at", { ascending: true });

        if (messages && messages.length > 0) {
          const { data: summaryResponse, error: summaryError } = await supabase.functions.invoke(
            "generate-chat-summary",
            {
              body: { messages },
            }
          );

          if (!summaryError && summaryResponse?.summary) {
            await supabase
              .from("chat_history")
              .update({ summary: summaryResponse.summary })
              .eq("id", chatId);
            
            chatHistory.summary = summaryResponse.summary;
          }
        }
      }

      return chatHistory as ChatHistory;
    },
    enabled: !!chatId && !!user?.id,
  });

  const { data: messages, isLoading: isLoadingMessages } = useQuery({
    queryKey: ["chat-messages", chatId],
    queryFn: async () => {
      if (!chatId || !chatData) return [];

      const { data, error } = await supabase
        .from("chat_messages")
        .select(`
          id,
          role,
          content,
          created_at,
          created_by,
          attachments,
          source,
          citations
        `)
        .eq("chat_history_id", chatId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      
      return data.map(message => ({
        ...message,
        attachments: message.attachments 
          ? (message.attachments as Json[])
              .map(jsonToFileAttachment)
              .filter((att): att is NonNullable<typeof att> => att !== null)
          : undefined
      })) as Message[];
    },
    enabled: !!chatData?.posits.id,
  });

  const formatDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) return "Date unknown";
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (!isValid(start) || !isValid(end)) return "Invalid date";
    
    // If same day
    if (start.toDateString() === end.toDateString()) {
      return `${format(start, "MMM d, yyyy")} ${format(start, "h:mm")} - ${format(end, "h:mm a")}`;
    }
    // Different days
    return `${format(start, "MMM d, yyyy h:mm a")} - ${format(end, "MMM d, yyyy h:mm a")}`;
  };

  if (isLoadingChat || isLoadingMessages) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!chatData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Chat Not Found</h1>
          <p className="text-muted-foreground mb-4">
            The chat conversation you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button asChild>
            <Link to="/dashboard/posits">Back to Posits</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="bg-card rounded-lg shadow-sm p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5" />
          <h1 className="text-2xl font-bold">{chatData.posits.title}</h1>
        </div>
        
        <div className="text-sm text-muted-foreground mb-4">
          {formatDateRange(chatData.created_at, chatData.last_message_at)}
        </div>

        {chatData.summary && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <h2 className="text-sm font-medium mb-2">Conversation Summary</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{chatData.summary}</p>
          </div>
        )}

        {/* Owners can start a new chat from this posit; non-owners see read-only history */}
        {user?.id && (chatData as any)?.posits?.user_id === user.id && (
          <Button asChild className="mt-6">
            <Link to={`/chat/${(chatData as any).posits.id}`} className="inline-flex items-center">
              Start New Chat
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>

      <h2 className="text-xl font-semibold mb-4">Chat History</h2>
      
      <div className="space-y-4">
        {messages?.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            userEmail={user?.email}
          />
        ))}
      </div>
    </div>
  );
};

export default ChatDetails;

